import {ChangeDetectionStrategy, Component, HostBinding, Inject, OnDestroy, OnInit, Optional, computed, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {DigitalInspectionApiService, DigitalInspectionReportResult} from '@app/services/api/digital-inspection-api.service';
import {V2LightboxComponent, V2LightboxImage} from '@app/shared/v2/v2-lightbox/v2-lightbox.component';
import {V2LocaleSwitcherComponent, V2LocaleOption} from '@app/shared/v2/v2-locale-switcher/v2-locale-switcher.component';
import {DAMAGE_INSPECTION_TRANSLATIONS, DEFAULT_DAMAGE_INSPECTION_LOCALE} from './damage-inspection-report-translations';
import {colorHexToName} from '@app/util/color-name.helper';
import {environment} from '../../../../../environments/environment';
import {NotificationService} from '@app/services/notification.service';

@Component({
  selector: 'app-damage-inspection-report-page',
  standalone: true,
  imports: [CommonModule, FormsModule, V2LightboxComponent, V2LocaleSwitcherComponent],
  templateUrl: './damage-inspection-report-page.component.html',
  styleUrls: ['./damage-inspection-report-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DamageInspectionReportPageComponent implements OnInit, OnDestroy {
  public isAvailable = signal(false);
  public reason = signal('');
  public reportData = signal<any>(null);

  public locale = signal(DEFAULT_DAMAGE_INSPECTION_LOCALE);

  public localeOptions: V2LocaleOption[] = [
    {code: 'sl', label: 'Slovenščina', flag: '🇸🇮'},
    {code: 'en', label: 'English', flag: '🇬🇧'},
    {code: 'hr', label: 'Hrvatski', flag: '🇭🇷'},
    {code: 'de', label: 'Deutsch', flag: '🇩🇪'},
  ];

  public shareCopied = signal(false);
  private shareCopiedTimeoutId: ReturnType<typeof setTimeout> | null = null;

  public async share(): Promise<void> {
    try {
      const url = `${window.location.origin}/r/damage-inspection/${this.vehicleUuid}`;
      await navigator.clipboard.writeText(url);
      this.shareCopied.set(true);
      if (this.shareCopiedTimeoutId !== null) {
        clearTimeout(this.shareCopiedTimeoutId);
      }
      this.shareCopiedTimeoutId = setTimeout(() => this.shareCopied.set(false), 2000);
    } catch (error) {
      console.error('Failed to copy share link to clipboard', error);
      this.notificationService.showErrorAlert(this.t('shareCopyFailed'), error as Error);
    }
  }

  private static readonly DYNAMIC_KEY_PREFIXES = ['status_', 'fuelType_', 'ageType_', 'transmissionType_'];

  public t(key: string): string {
    const translated = DAMAGE_INSPECTION_TRANSLATIONS[this.locale()]?.[key] ?? DAMAGE_INSPECTION_TRANSLATIONS[DEFAULT_DAMAGE_INSPECTION_LOCALE][key];
    if (translated !== undefined) {
      return translated;
    }
    /**
     * Fallback for dynamic, backend-driven keys built as `<prefix><rawValue>` — currently
     * covers the `status_`, `fuelType_`, `ageType_` and `transmissionType_` families (see
     * DYNAMIC_KEY_PREFIXES above). When none of these keys is mapped in
     * DAMAGE_INSPECTION_TRANSLATIONS, we strip the known prefix and render the raw backend
     * value as-is rather than an empty string: this is a public-facing report, and showing
     * the untranslated-but-real value (e.g. "diesel") is more useful to the reader than
     * showing nothing at all. If you add a new dynamic-key category elsewhere in this
     * component, you must add its prefix to DYNAMIC_KEY_PREFIXES too, or its fallback will
     * silently render the full prefixed key instead of just the value.
     */
    const matchedPrefix = DamageInspectionReportPageComponent.DYNAMIC_KEY_PREFIXES.find((prefix) => key.startsWith(prefix));
    return matchedPrefix ? key.slice(matchedPrefix.length) : key;
  }

  public setLocale(locale: string): void {
    if (DAMAGE_INSPECTION_TRANSLATIONS[locale]) {
      this.locale.set(locale);
    }
  }

  public exteriorDamages = computed<any[]>(() =>
    (this.reportData()?.damages ?? []).filter((d: any) => d?.damageLocation === 'exterior'),
  );

  public interiorDamages = computed<any[]>(() =>
    (this.reportData()?.damages ?? []).filter((d: any) => d?.damageLocation === 'interior'),
  );

  public otherDamages = computed<any[]>(() =>
    (this.reportData()?.damages ?? []).filter(
      (d: any) => d?.damageLocation !== 'exterior' && d?.damageLocation !== 'interior',
    ),
  );

  /** Flattening order the template's per-section lightbox index math relies
   *  on — must stay exterior, then interior, then other. */
  public orderedDamages = computed<any[]>(() => [
    ...this.exteriorDamages(),
    ...this.interiorDamages(),
    ...this.otherDamages(),
  ]);

  public lightboxIndex = -1;
  public lightboxImages = computed<V2LightboxImage[]>(() => {
    const damages = this.orderedDamages();
    const items: V2LightboxImage[] = [];
    for (const damage of damages) {
      for (const image of this.imagesWithNormal(damage)) {
        items.push({src: image.imageUrlNormal});
      }
    }
    return items;
  });

  public damageImageOffsets = computed<number[]>(() => {
    const damages = this.orderedDamages();
    const offsets: number[] = [];
    let running = 0;
    for (const damage of damages) {
      offsets.push(running);
      running += this.imagesWithNormal(damage).length;
    }
    return offsets;
  });

  public tagPills = computed<string[]>(() => {
    const seen = new Set<string>();
    const pills: string[] = [];
    for (const damage of this.reportData()?.damages ?? []) {
      const part = damage?.objectConditionTemplateSidePart;
      if (part && !seen.has(part)) {
        seen.add(part);
        pills.push(part);
      }
    }
    return pills;
  });

  public reportInspector = computed<{name: string; date: string} | null>(() => {
    const first = (this.reportData()?.damages ?? [])[0];
    if (!first?.inspectionUserFullName) {
      return null;
    }
    return {name: first.inspectionUserFullName, date: first.inspectionTime};
  });

  public reportReviewer = computed<{name: string; date: string} | null>(() => {
    const reviewed = (this.reportData()?.damages ?? []).find((d: any) => !!d?.revisionUserFullName);
    if (!reviewed) {
      return null;
    }
    return {name: reviewed.revisionUserFullName, date: reviewed.revisionTime};
  });

  public colorLabel(): string | null {
    return colorHexToName(this.reportData()?.vehicle?.colorName ?? null);
  }

  /** Backend never sends HP — reuses the app-wide `kwToHpRatio` constant (same
   *  value form-input-engine-power / field-table-value / vehicle-import each
   *  multiply by directly) rather than introducing a second, possibly-inconsistent
   *  ratio. Only the constant is shared, not a computation function — each site
   *  independently does `Math.round(kw * kwToHpRatio)`. */
  public enginePowerHp(): number | null {
    const kw = this.reportData()?.vehicle?.enginePowerKw;
    return kw ? Math.round(kw * environment.kwToHpRatio) : null;
  }

  /**
   * Returns only the images of a damage that have an `imageUrlNormal` field, in the same
   * order used by `lightboxImages`/`damageImageOffsets`. The template's thumbnail
   * buttons must iterate over this same list (not the raw `inspectionImages`) so
   * that `photoIndex` stays aligned with the flat lightbox list.
   */
  public imagesWithNormal(damage: any): any[] {
    return (damage?.inspectionImages ?? []).filter((img: any) => img?.imageUrlNormal);
  }

  public openLightboxForDamage(damageIndex: number, photoIndexWithinDamage: number): void {
    this.lightboxIndex = this.damageImageOffsets()[damageIndex] + photoIndexWithinDamage;
  }

  public tyreLocationLabel(tyre: any): string {
    return this.t(`tyreLocation_${tyre?.tyreLocation}`);
  }

  /** True when opened as a `MatDialog` (from the vehicle page) rather than
   *  routed to directly (the public share-link page) — controls the close
   *  button and skips the route-resolver data source below. */
  public isDialogMode = false;

  /** Vehicle UUID used to build the public share link — from dialog data in
   *  dialog mode (the vehicle page never navigates here), or the route param
   *  in routed/public mode. Needed because `window.location.href` is only a
   *  valid share link in routed mode; in dialog mode it's the authenticated
   *  vehicle-page URL. */
  private vehicleUuid = '';

  constructor(
    private activatedRoute: ActivatedRoute,
    private digitalInspectionApi: DigitalInspectionApiService,
    private notificationService: NotificationService,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData: {result: DigitalInspectionReportResult; vehicleUuid: string} | null,
    @Optional() private dialogRef: MatDialogRef<DamageInspectionReportPageComponent> | null,
  ) {
    this.isDialogMode = !!this.dialogData;
  }

  ngOnInit(): void {
    // Opened as a dialog (from the vehicle page): the parent already fetched
    // the report while checking availability, and passes that result in
    // directly — no second fetch, no route resolver involved. Opened as the
    // public share-link route: data comes from PublicDamageInspectionResolver
    // as before.
    const result = this.dialogData?.result ?? (this.activatedRoute.snapshot.data.result as DigitalInspectionReportResult);
    this.vehicleUuid = this.dialogData?.vehicleUuid ?? (this.activatedRoute.snapshot.paramMap.get('vehicleUuid') ?? '');
    this.applyResult(result);
  }

  public closeDialog(): void {
    this.dialogRef?.close();
  }

  ngOnDestroy(): void {
    if (this.shareCopiedTimeoutId !== null) {
      clearTimeout(this.shareCopiedTimeoutId);
    }
  }

  /** True while a PDF is being generated, so the buttons can't be double-clicked. */
  public pdfBusy = signal(false);

  /** The routed public page owns its scroll container (the global `body { overflow: hidden }`
   *  would otherwise clip it); in the dialog the dialog surface scrolls instead. */
  @HostBinding('class.di-report-host--page')
  public get isPageHost(): boolean {
    return !this.isDialogMode;
  }

  /** Opens the generated PDF's print dialog — the same document as the download, wherever the
   *  report is opened from, instead of a browser print of the screen layout. */
  public print(): void {
    this.fetchPdf((blob) => {
      const url = URL.createObjectURL(blob);
      const frame = document.createElement('iframe');
      frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
      const cleanup = () => {
        frame.remove();
        URL.revokeObjectURL(url);
      };
      frame.onload = () => {
        try {
          frame.contentWindow?.focus();
          frame.contentWindow?.print();
          // Keep the frame alive while the print dialog is open; it is released with the page.
          setTimeout(cleanup, 60_000);
        } catch (error) {
          // Some browsers refuse to print a PDF inside a frame — show it in a tab instead.
          cleanup();
          this.openPdfInNewTab(blob);
        }
      };
      frame.src = url;
      document.body.appendChild(frame);
    });
  }

  public downloadPdf(): void {
    this.fetchPdf((blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `damage-report-${this.reportData()?.vehicle?.vin ?? this.vehicleUuid}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    });
  }

  private openPdfInNewTab(blob: Blob): void {
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  private fetchPdf(onBlob: (blob: Blob) => void): void {
    if (this.pdfBusy()) {
      return;
    }
    this.pdfBusy.set(true);

    const request$ = this.isDialogMode
      ? this.digitalInspectionApi.getAuthenticatedReportPdf(this.vehicleUuid, this.locale())
      : this.digitalInspectionApi.getPublicReportPdf(this.vehicleUuid, this.locale());

    request$.subscribe({
      next: (blob) => {
        this.pdfBusy.set(false);
        onBlob(blob);
      },
      error: (error) => {
        this.pdfBusy.set(false);
        console.error('Failed to create the damage report PDF', error);
        this.notificationService.showErrorAlert(this.t('pdfFailed'), error as Error);
      },
    });
  }

  private applyResult(result: DigitalInspectionReportResult): void {
    if ('data' in result) {
      this.isAvailable.set(true);
      this.reportData.set(result.data);

      const accountLocale = result.data?.accountLocale;
      if (accountLocale && DAMAGE_INSPECTION_TRANSLATIONS[accountLocale]) {
        this.locale.set(accountLocale);
      }
    } else {
      this.isAvailable.set(false);
      this.reason.set(result.reason);
    }
  }
}
